import React, { useState, useEffect } from 'react';
import { Select, DatePicker, Button, Table, message } from 'antd';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/fire';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { FaFilePdf, FaFileExcel } from 'react-icons/fa';

const { Option } = Select;
const { RangePicker } = DatePicker;

const StaffReport = ({ isSecAdmin, theme = 'light' }) => {
  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [dateRange, setDateRange] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCoachName, setSelectedCoachName] = useState('');
  const [formattedDateRange, setFormattedDateRange] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    fetchCoaches();
  }, []);

  const fetchCoaches = async () => {
    try {
      const coachesRef = collection(db, 'coaches');
      const coachesSnapshot = await getDocs(coachesRef);
      const coachesData = coachesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCoaches(coachesData);
    } catch (error) {
      console.error("Error fetching coaches:", error);
      message.error('Failed to fetch coaches');
    }
  };

  const checkScheduledClasses = async (coachName, startDate, endDate) => {
    try {
      const classesQuery = query(
        collection(db, 'classSchedule'),
        where('coaches', 'array-contains', coachName)
      );
      const querySnapshot = await getDocs(classesQuery);
      const scheduledClasses = [];

      const now = moment();

      querySnapshot.forEach((doc) => {
        const classData = doc.data();
        if (classData.isSpecialClass) {
          const date = moment(classData.specialClassDate);
          if (date.isBetween(startDate, endDate, null, '[]') && date.isSameOrBefore(now)) {
            scheduledClasses.push({
              date: date.format('YYYY-MM-DD'),
              className: classData.className,
              startTime: classData.startTime,
              endTime: classData.endTime
            });
          }
        } else {
          const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday',
            'Thursday', 'Friday', 'Saturday'];
          classData.days.forEach(day => {
            let date = moment(startDate).day(daysOfWeek.indexOf(day));
            while (date.isSameOrBefore(endDate) && date.isSameOrBefore(now)) {
              scheduledClasses.push({
                date: date.format('YYYY-MM-DD'),
                className: classData.className,
                startTime: classData.startTime,
                endTime: classData.endTime
              });
              date.add(1, 'week');
            }
          });
        }
      });

      return scheduledClasses;
    } catch (error) {
      console.error("Error checking scheduled classes:", error);
      return [];
    }
  };
  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };


  const fetchWorklogData = async (coachId, startDate, endDate) => {
    try {
      const rangeStart = moment(startDate).startOf('day');
      const rangeEnd = moment(endDate).endOf('day');

      const [worklogSnapshot, scheduledClasses] = await Promise.all([
        getDocs(query(
          collection(db, 'worklog'),
          where('__name__', '>=', rangeStart.format('YYYY-MM-DD')),
          where('__name__', '<=', rangeEnd.format('YYYY-MM-DD'))
        )),
        checkScheduledClasses(selectedCoachName, rangeStart, rangeEnd)
      ]);


      const report = [];
      const coachDoc = await getDoc(doc(db, "coaches", coachId));

      if (!coachDoc.exists()) {
        console.error("Coach document not found");
        return;
      }
      const coachData = coachDoc.data();

      worklogSnapshot.forEach((doc) => {
        const date = doc.id;
        const coachLogs = doc.data()[coachData.name];

        if (coachLogs) {
          Object.entries(coachLogs).forEach(([classId, classData]) => {
            const classEntries = Array.isArray(classData) ? classData : [classData];

            classEntries.forEach((entry, entryIndex) => {
              const scheduledStart = moment(`${date} ${entry.scheduledStart}`, 'YYYY-MM-DD HH:mm');
              const scheduledEnd = moment(`${date} ${entry.scheduledEnd}`, 'YYYY-MM-DD HH:mm');

              let actualLogin;
              if (entry.loginTime && entry.loginTime.toDate instanceof Function) {
                actualLogin = moment(entry.loginTime.toDate());
              } else {
                console.error(`Invalid loginTime for class ${classId} on ${date}`);
                actualLogin = scheduledStart;
              }


              const timeDiff = actualLogin.diff(scheduledStart, 'minutes');
              let scheduledDuration = scheduledEnd.diff(scheduledStart, 'minutes');
              let actualDuration;

              if (timeDiff < 0) {
                actualDuration = scheduledDuration;
              } else {
                actualDuration = Math.max(0, scheduledEnd.diff(actualLogin, 'minutes'));
              }

              report.push({
                key: `${date}-${classId}-${entryIndex}`,
                date: date,
                className: entry.className,
                scheduledStartTime: scheduledStart.format('HH:mm'),
                scheduledEndTime: scheduledEnd.format('HH:mm'),
                scheduledDuration: `${scheduledDuration} mins`,
                actualLoginTime: actualLogin.format('HH:mm'),
                timeDifference: timeDiff === 0 ? "On time" : `${Math.abs(timeDiff)} minute${Math.abs(timeDiff) !== 1 ? 's' : ''} ${timeDiff < 0 ? "early" : "late"}`,
                actualDuration: `${actualDuration} mins`,
              });
            });
          });
        }
      });

      const filteredScheduled = scheduledClasses.filter(c =>
        moment(c.date).isBetween(rangeStart, rangeEnd, null, '[]')
      );

      filteredScheduled.forEach(scheduledClass => {
        if (!worklogSnapshot.docs.some(d => d.id === scheduledClass.date)) {
          const start = moment(scheduledClass.startTime, 'HH:mm');
          const end = moment(scheduledClass.endTime, 'HH:mm');
          const duration = end.diff(start, 'minutes');

          report.push({
            key: `${scheduledClass.date}-${scheduledClass.className}-absent`,
            date: scheduledClass.date,
            className: scheduledClass.className,
            scheduledStartTime: scheduledClass.startTime,
            scheduledEndTime: scheduledClass.endTime,
            scheduledDuration: `${duration} mins`,
            actualLoginTime: 'Absent',
            timeDifference: 'Absent',
            actualDuration: '0 mins'
          });
        }
      });


      const uniqueReport = report.filter((v, i, a) =>
        a.findIndex(t => t.key === v.key) === i
      );

      setReportData(uniqueReport.sort((a, b) => moment(b.date).diff(moment(a.date))));
      setFormattedDateRange(
        startDate && endDate
          ? `${moment(startDate).format('YYYY-MM-DD')} to ${moment(endDate).format('YYYY-MM-DD')}`
          : ''
      );
    } catch (error) {
      console.error("Error fetching worklog data:", error);
      message.error('Failed to generate report');
    }
  };

  const handleCoachSelect = (value) => {
    setSelectedCoach(value);
    const selectedCoachData = coaches.find(coach => coach.id === value);
    setSelectedCoachName(selectedCoachData ? selectedCoachData.name : '');
  };
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates && dates.length === 2) {
      setFormattedDateRange(`${dates[0].format('YYYY-MM-DD')} to ${dates[1].format('YYYY-MM-DD')}`);
    }
  };
  const ReportHeader = () => (
    <div style={{ marginBottom: 16 }} className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
      <h2>Coach: {selectedCoachName}</h2>
      <h3>Period: {formattedDateRange}</h3>
    </div>
  );
  const generateReport = async () => {
    if (!selectedCoach || !startDate || !endDate) {
      message.warning('Please select a Instructor and date range');
      return;
    }

    setLoading(true);
    try {
      await fetchWorklogData(
        selectedCoach,
        startDate.toDate(),
        endDate.toDate()
      );
    } catch (error) {
      console.error("Error generating report:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: 'Date', dataIndex: 'date', key: 'date' },
    { title: 'Class Name', dataIndex: 'className', key: 'className' },
    { title: 'Scheduled Start Time', dataIndex: 'scheduledStartTime', key: 'scheduledStartTime' },
    { title: 'Scheduled End Time', dataIndex: 'scheduledEndTime', key: 'scheduledEndTime' },
    { title: 'Scheduled Duration', dataIndex: 'scheduledDuration', key: 'scheduledDuration' },
    { title: 'Actual Login Time', dataIndex: 'actualLoginTime', key: 'actualLoginTime' },
    {
      title: 'Time Difference',
      dataIndex: 'timeDifference',
      key: 'timeDifference',
      render: (text) => {
        const isLate = text.includes('late');
        return isLate ? <span style={{ color: 'red', fontWeight: 'bold' }}>{text}</span> : text;
      }
    },
    { title: 'Actual Duration', dataIndex: 'actualDuration', key: 'actualDuration' },
  ];

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text(`Coach: ${selectedCoachName}`, 14, 15);
    doc.text(`Period: ${formattedDateRange}`, 14, 25);
    doc.autoTable({
      startY: 35,
      head: [columns.map(col => col.title)],
      body: [
        ...reportData.map(item => columns.map(col => item[col.dataIndex])),
        ['Total', '', '', '', totals.scheduledHours, '', '', totals.actualHours]
      ],
    });
    doc.save(`staff_report_${selectedCoachName}.pdf`);
  };


  const exportToExcel = () => {

    const ws = XLSX.utils.json_to_sheet([
      { A: `Coach: ${selectedCoachName}` },
      { A: `Period: ${formattedDateRange}` },
      {},  // Empty row for spacing
      { A: 'Date', B: 'Class Name', C: 'Scheduled Start Time', D: 'Scheduled End Time', E: 'Scheduled Duration', F: 'Actual Login Time', G: 'Time Difference', H: 'Actual Duration' }, // Headers
      ...reportData.map(item => ({
        A: item.date,
        B: item.className,
        C: item.scheduledStartTime,
        D: item.scheduledEndTime,
        E: item.scheduledDuration,
        F: item.actualLoginTime,
        G: item.timeDifference,
        H: item.actualDuration
      }))
    ]);

    // Create workbook and append worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Staff Report");

    // Add total row with proper alignment
    const totalRow = [
      { v: 'Total', t: 's' }, '', '', '', totals.scheduledHours, '', '', totals.actualHours
    ];
    XLSX.utils.sheet_add_aoa(ws, [totalRow], { origin: -1 });

    XLSX.writeFile(wb, "staff_report.xlsx");
  };
  const calculateTotalHours = (key) => {
    return reportData.reduce((acc, curr) => {
      if (curr[key] && curr[key] !== 'Absent') {
        const duration = parseInt(curr[key].split(' ')[0]) || 0;
        return acc + duration;
      }
      return acc;
    }, 0);
  };

  const calculateTotalLateMinutes = () => {
    return reportData.reduce((acc, curr) => {
      // Only count if timeDifference contains "late"
      if (curr.timeDifference && curr.timeDifference.includes('late')) {
        // Extract the number of minutes from the string (e.g., "28 minutes late")
        const match = curr.timeDifference.match(/(\d+)\s*minute/);
        if (match) {
          return acc + parseInt(match[1], 10);
        }
      }
      return acc;
    }, 0);
  };

  const formatLateMinutes = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours > 0 ? `${hours} hr ${minutes} min` : `${minutes} min`;
  };


  const formatDuration = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours} hr ${minutes} min`;
  };

  const totals = {
    scheduledHours: formatDuration(calculateTotalHours('scheduledDuration')),
    actualHours: formatDuration(calculateTotalHours('actualDuration')),
    lateMinutes: formatLateMinutes(calculateTotalLateMinutes()),
  };

  return (
    <div className={`p-4 sm:p-6 min-h-screen ${
      theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
    }`}>
      <div className={`rounded-lg shadow-md p-4 sm:p-6 mb-6 ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="flex flex-col md:flex-row gap-4 md:items-end">
          <div className="flex-1 min-w-[200px]">
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}>Instructor</label>
            <Select
              className={`w-full ${theme === 'dark' ? 'dark-select' : ''}`}
              dropdownClassName={theme === 'dark' ? 'dark-select-dropdown' : ''}
              placeholder="Select Instructor"
              showSearch
              optionFilterProp="children"
              disabled={isSecAdmin}
              onChange={handleCoachSelect}
              size="large"
            >
              {coaches.map(coach => (
                <Option key={coach.id} value={coach.id}>{coach.name}</Option>
              ))}
            </Select>
          </div>

          <div className="flex-1 min-w-[180px]">
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}>Start Date</label>
            <DatePicker
              className={`w-full ${theme === 'dark' ? 'dark-datepicker' : ''}`}
              placeholder="Select Start Date"
              onChange={handleStartDateChange}
              disabled={isSecAdmin}
              format="YYYY-MM-DD"
              value={startDate}
              size="large"
            />
          </div>

          <div className="flex-1 min-w-[180px]">
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}>End Date</label>
            <DatePicker
              className={`w-full ${theme === 'dark' ? 'dark-datepicker' : ''}`}
              placeholder="Select End Date"
              onChange={handleEndDateChange}
              disabled={isSecAdmin}
              format="YYYY-MM-DD"
              value={endDate}
              size="large"
            />
          </div>

          <Button
            type="primary"
            onClick={generateReport}
            disabled={isSecAdmin}
            loading={loading}
            size="large"
            className="bg-blue-600 hover:bg-blue-700 h-11 px-8 font-semibold"
          >
            Generate Report
          </Button>
        </div>
      </div>

      {reportData.length > 0 ? (
        <div className={`rounded-lg shadow-md p-4 sm:p-6 mb-6 ${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white'
        }`}>
          <div className="mb-6">
            <h2 className={`text-xl font-bold mb-1 ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-800'
            }`}>Instructor: {selectedCoachName}</h2>
            <p className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Period: {formattedDateRange}
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <Table
              dataSource={reportData}
              columns={columns}
              pagination={{ 
                position: ['bottomCenter'], 
                pageSize: 10,
                showSizeChanger: false,
                className: 'mt-4'
              }}
              scroll={{ x: 'max-content' }}
              bordered
              size="middle"
              className={theme === 'dark' ? 'dark-table' : ''}
              summary={() => (
                <Table.Summary>
                  <Table.Summary.Row className={theme === 'dark' ? 'bg-gray-700' : 'bg-blue-50'}>
                    <Table.Summary.Cell index={0} colSpan={4}>
                      <strong className={theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}>Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} colSpan={2}>
                      <strong className={theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}>{totals.scheduledHours}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>
                      <span className="text-red-600 font-bold">
                        Late: {totals.lateMinutes}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={7}>
                      <strong className={theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}>{totals.actualHours}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </div>
          
          {reportData.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-3">
              <Button 
                disabled={isSecAdmin} 
                onClick={exportToExcel}
                size="large"
                className="bg-green-600 hover:bg-green-700 text-white border-0 font-semibold flex items-center gap-2"
              >
                <FaFileExcel /> Export to Excel
              </Button>
              <Button 
                disabled={isSecAdmin} 
                onClick={exportToPDF}
                size="large"
                className="bg-red-600 hover:bg-red-700 text-white border-0 font-semibold flex items-center gap-2"
              >
                <FaFilePdf /> Export to PDF
              </Button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default StaffReport;